import { __awaiter } from "tslib";
import { ApolloClient, ApolloLink, HttpLink } from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { onError } from '@apollo/client/link/error';
import { has } from 'lodash-es';
import { clearAuth, getAuth, setAuth } from '@marvelapp/askhub-auth-ui';
import { getUrlEncodedRequestBody } from '@marvelapp/core';
import config from './config';
export default function createGqlClient({ accessToken: initialAccessToken, }) {
    let accessToken = initialAccessToken;
    let refreshingPromise = null;
    function refreshAccessToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const auth = getAuth(config);
            if (!auth || !auth.refreshToken) {
                throw new Error('No refresh token available');
            }
            const formBody = getUrlEncodedRequestBody({
                grant_type: 'refresh_token',
                refresh_token: auth.refreshToken,
                client_id: config.pie.clientId,
            });
            const response = yield fetch(`${config.pie.host}/oauth/token/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: formBody,
            });
            const data = yield response.json();
            const { access_token: newAccessToken, refresh_token: newRefreshToken, expires_in: expiresIn, scope, } = data;
            const expiry = Date.now() + parseInt(expiresIn) * 1000;
            setAuth({
                accessToken: newAccessToken,
                refreshToken: newRefreshToken,
                expiry,
                scopes: scope.split(' '),
            });
            return newAccessToken;
        });
    }
    function fetchAndRefreshToken(uri, options) {
        return fetch(uri, options).then((response) => __awaiter(this, void 0, void 0, function* () {
            if (response.status !== 401) {
                return response;
            }
            // Handle token refresh
            if (!refreshingPromise) {
                refreshingPromise = refreshAccessToken().catch(() => {
                    clearAuthAndRedirectToLoginPage();
                });
            }
            return refreshingPromise.then((newAccessToken) => {
                refreshingPromise = null;
                accessToken = newAccessToken;
                const newOptions = Object.assign(Object.assign({}, options), { headers: Object.assign(Object.assign({}, options === null || options === void 0 ? void 0 : options.headers), { 
                        // options.headers we get from `HTTPLink` are normalized to lowercase
                        // so we need to use lowercase authorization here
                        authorization: `Bearer ${accessToken}` }) });
                return fetch(uri, newOptions);
            });
        }));
    }
    const authorizationMiddleware = new ApolloLink((operation, forward) => {
        const context = operation.getContext();
        // Don't ovveride the authorization header if the access token is undefined
        // or if the query/mutation has a custom authorization defined in the context
        if (accessToken && !has(context, 'headers.Authorization')) {
            operation.setContext({
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
        }
        return forward(operation);
    });
    return new ApolloClient({
        link: ApolloLink.from([
            onError(({ graphQLErrors }) => {
                if (graphQLErrors) {
                    graphQLErrors.map(({ message, locations, path }) => 
                    // eslint-disable-next-line no-console
                    console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`));
                }
            }),
            authorizationMiddleware,
            new HttpLink({
                fetch: fetchAndRefreshToken,
                uri: `${config.pie.host}/graphql`,
                credentials: 'same-origin',
            }),
        ]),
        cache: new InMemoryCache({
            // The default implementation of dataIdFromObject looks for ID fields
            // named `id` or `_id`, so we override it to use `pk` instead
            dataIdFromObject(result) {
                if (result.__typename && result.pk) {
                    return `${result.__typename}:${result.pk}`;
                }
                if (result.__typename === 'BillingNode') {
                    return `${result.__typename}:${result.subscriptionPk}`;
                }
                if (result.__typename === 'StudyOrderDetailsNode') {
                    return `${result.__typename}:${result.studyId}`;
                }
                if (result.__typename === 'AggregateResultStats') {
                    return `${result.__typename}:${result.testPk}`;
                }
                if (result.__typename === 'Seats') {
                    return result.__typename;
                }
                return undefined;
            },
            typePolicies: {
                LiveSessionNode: {
                    keyFields: ['testRecording', ['roomName']],
                },
                StudyOrderDetailsNode: {
                    fields: {
                        unsupportedFilters: {
                            merge(_, incoming) {
                                return incoming;
                            },
                        },
                    },
                },
                PrivateUserNode: {
                    fields: {
                        properties: {
                            merge: true,
                        },
                    },
                },
                CompanyNode: {
                    fields: {
                        settings: {
                            merge: true,
                        },
                    },
                },
                ProjectSettingsNode: {
                    fields: {
                        settings: {
                            merge: true,
                        },
                    },
                },
            },
            possibleTypes: {
                AnswerTypeUnion: [
                    'AnswerPlainText',
                    'AnswerEmail',
                    'AnswerBoolean',
                    'AnswerDateTime',
                    'AnswerRating',
                    'AnswerMultiPreference',
                    'AnswerInteger',
                ],
            },
        }),
        defaultOptions: {
            watchQuery: {
                notifyOnNetworkStatusChange: true,
                fetchPolicy: 'cache-and-network',
                // See https://github.com/marvelapp/mkiii/pull/3242 for the convoluted explanation
                // for why we need this
                nextFetchPolicy: 'cache-first',
            },
        },
    });
}
function clearAuthAndRedirectToLoginPage() {
    clearAuth();
    const params = new URLSearchParams();
    params.append('next', window.location.href);
    params.append('no-redirect', '');
    window.location.href = `${config.ballpark.marketingSite}/sign-in?${params.toString()}`;
}
